import { ServicePluginDefinition } from '@wix/sdk-types';
import {
  Context,
  ApplyPointsLimitRequest,
  ApplyPointsLimitResponse,
} from './service-plugins-types.js';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';

export interface ApplyPointsLimitEnvelope {
  request: ApplyPointsLimitRequest;
  metadata: Context;
}

export const provideHandlers = ServicePluginDefinition<{
  /**
   *
   * Returns the number of loyalty points the contact may earn from the order
   * after applying the limit logic.
   *
   * Wix calls this method when a contact is about to earn loyalty points from
   * a paid eCommerce order, after any points-adjustment logic has run. The
   * method runs for orders placed through Wix Stores, Wix Bookings, and other
   * apps that use the Wix eCommerce checkout. It doesn't run for points earned
   * through other triggers, such as Wix Automations actions.
   *
   * Wix uses the returned `adjustedPointsToEarn` value as the final number of
   * points to assign for the order. When the returned value is `0`, an earn
   * attempt is recorded in the contact's loyalty activity history and no
   * points are assigned.
   *
   * **Error handling:** If the implementation returns an error or times out,
   * Wix logs the failure and assigns `pointsToEarn` unchanged. No retries are
   * performed at the service plugin layer. */
  applyPointsLimit(
    payload: ApplyPointsLimitEnvelope
  ): ApplyPointsLimitResponse | Promise<ApplyPointsLimitResponse>;
}>('LOYALTY_EARN_POINTS_LIMIT', [
  {
    name: 'applyPointsLimit',
    primaryHttpMappingPath: '/v1/apply-points-limit',
    transformations: {
      toREST: (payload: any) => {
        const toRestResponse = payload;

        return renameKeysFromSDKRequestToRESTRequest(toRestResponse);
      },
      fromREST: (payload: any) => {
        const fromRestRequest = transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'request.account.createdDate' }],
          },
        ]);

        return renameKeysFromRESTResponseToSDKResponse(fromRestRequest);
      },
    },
  },
]);
